import {DovizTakipSekli} from "@/enum/DovizTakipSekli";
import {ParaBirimi} from "@/enum/ParaBirimi";

/**
 * DEV-2832 — Dövizli takiplerde UYAP'ın takip talebi / icra emri / ödeme emri
 * metninde her alacak kaleminin ve toplam satırının yanına bastığı tahsil şekli
 * şerhi. ICW belgeyi kendisi ürettiği için (UYAP değil) bu şerh basılmıyordu.
 *
 * ŞERH METNİNİN KAYNAĞI: DEV-2832 kaydındaki UYAP / e-İcraPro çıktısı. Repoda
 * bağımsız bir kopyası yok; metin değişirse tek düzeltme noktası aşağıdaki sabit.
 *
 * EŞLEME — UYAP'ta dövizli alacakta "Hesap Seçeneği" iki değer alır: "Takip
 * Tarihi" ve "Fiili Ödeme Tarihi". ICW'deki karşılığı `alacaklar.doviz_kur_tarihi`
 * (enum: vade | takip | tahsilat — UI'daki adı "Esas Alınan Kur",
 * bkz. DovizTakipSekliPicker.vue).
 *
 * `tahsilat` = UYAP'ın "Fiili Ödeme Tarihi" seçeneğidir: asıl alacak TL'ye
 * çevrilmez, takip öncesi ve takip sonrası faiz döviz üzerinden işler. Bu eşleme
 * üç bağımsız yerde aynı şekilde kurulu:
 *   - KapakHesabi.ts (tahsilat dalı TL'ye çevirmiyor)
 *   - backend Takip::getTakipToplami / getHarcTakipToplami
 *   - backend VekaletUcretHesaplayici (yalnız takip|vade TL'ye çevriliyor)
 * Hesap doğru yapılıyordu, belgeye şerh düşülmüyordu; bu modül o eksiği kapatır.
 *
 * `takip` ve `vade` için UYAP'ın ayrı bir şerh bastığına dair elimizde örnek
 * çıktı yok (kod, dokümanlar ve DB dump'ları tarandı — tek eşleşme yok). Uydurma
 * metin basmaktansa basmamak doğru; örnek geldiğinde SERHLER haritasına tek satır
 * eklemek yeterlidir.
 */
export const FIILI_ODEME_SERHI =
    "(Fiili Ödeme Tarihindeki Türk Lirası Karşılığı Üzerinden Tahsili Talebi)";

/** Toplam satırının mevcut etiketi (ICW'de parantezli basılıyor). */
export const TOPLAM_ALACAK_ETIKETI = "(Toplam Alacak)";

const SERHLER: { [key: string]: string } = {
    [DovizTakipSekli.tahsilat]: FIILI_ODEME_SERHI,
};

/**
 * Şerh bu satırda zaten var mı? Otomatik şerh gelmeden önce kullanıcılar aynı
 * ibareyi 4. maddeye ELLE ekliyordu (alt tip etiketi / alacak açıklaması) —
 * destek kayıtlarında "Takip talebi 4.maddeye fiili ödeme günündeki kur üzerinden
 * ibaresi eklemeleri için ilgili alana yönlendirdim" gibi çok sayıda örnek var.
 * O kayıtlar için şerhi ikinci kez basmamak gerekiyor; bu yüzden birebir metin
 * yerine "fiili ödeme" çekirdeği aranır (elle yazımlar birbirinden farklı:
 * "fiili ödeme günündeki kur üzerinden", "fiili ödeme tarihindeki kur", ...).
 */
const SERH_CEKIRDEGI = "fiili odeme";

const TURKCE_HARFLER: { [key: string]: string } = {
    "İ": "i", "I": "i", "ı": "i", "Î": "i", "î": "i",
    "Ö": "o", "ö": "o", "Ô": "o", "ô": "o",
    "Ü": "u", "ü": "u", "Û": "u", "û": "u",
    "Â": "a", "â": "a",
    "Ş": "s", "ş": "s",
    "Ğ": "g", "ğ": "g",
    "Ç": "c", "ç": "c"
};

/** Türkçe harf farkları, şapkalı yazımlar ve çoklu boşluk elenir ("fiilî  ödeme" → "fiili odeme"). */
function normalize(metin: string): string {
    return metin
        .replace(/[İIıÎîÖöÔôÜüÛûÂâŞşĞğÇç]/g, ch => TURKCE_HARFLER[ch] || ch)
        .toLowerCase()
        .replace(/\s+/g, " ");
}

export function serhZatenVarMi(metin: string | null | undefined): boolean {
    if (!metin) {
        return false;
    }
    return normalize(String(metin)).indexOf(SERH_CEKIRDEGI) !== -1;
}

/**
 * İlgili alacak kalemine düşecek şerhi döner. TL kalemlerde ve şerh gerektirmeyen
 * kur seçeneklerinde boş string döner.
 */
export function dovizTahsilSerhi(
    dovizKurTarihi: DovizTakipSekli | string | null | undefined,
    paraBirimiId: number | string | null | undefined
): string {
    if (!dovizKurTarihi) {
        return "";
    }
    if (paraBirimiId === null || paraBirimiId === undefined || paraBirimiId === "") {
        return "";
    }
    if (Number(paraBirimiId) === ParaBirimi.TRY) {
        return "";
    }
    return SERHLER[String(dovizKurTarihi)] || "";
}

/**
 * Açıklama metninin sonuna şerhi ekler. Şerh yoksa metni aynen döner; metin şerhi
 * (birebir ya da elle yazılmış bir varyantını) zaten içeriyorsa tekrar eklemez.
 *
 * DİKKAT: Dönen değer düz metindir ve şablonda mevcut `<span>`in İÇİNE basılır.
 * Şerhi ayrı bir blok eleman (`<div>`/`<p>`) olarak eklemeyin — html2udf
 * dönüşümünde hücrede blok çocuk bulunduğunda kardeş inline içerik sessizce
 * düşüyor (bkz. html2udf.js handleTable, childBlocks dalı).
 */
export function serhEkle(aciklama: string | null | undefined, serh: string): string {
    // Şerh basılmayan yol (TL kalem, takip/vade seçeneği, seçim yapılmamış) DEĞİŞMEMELİ:
    // metni trim dahi etmeden aynen döneriz. html2udf metin düğümlerindeki baştaki/sondaki
    // boşlukları koruduğu için trim, şerhsiz belgelerde sessiz bir çıktı farkı yaratırdı.
    if (!serh) {
        return (aciklama === null || aciklama === undefined) ? "" : String(aciklama);
    }
    const metin = (aciklama === null || aciklama === undefined) ? "" : String(aciklama).trim();
    // Açıklaması boş bir satıra ÇIPLAK şerh basmayız: şablonların bir kısmı
    // `aciklama || masraf_cesidi` fallback'i kullanıyor ve boş açıklamanın truthy hale
    // gelmesi kalemin adını belgeden düşürürdü. Adı olmayan bir satırda tek başına şerh
    // zaten anlamsız; metin boşsa satırı olduğu gibi bırakırız.
    if (metin.length === 0) {
        return metin;
    }
    if (serhZatenVarMi(metin)) {
        return metin;
    }
    return metin + " " + serh;
}

/**
 * Toplam satırının etiketini şerhi ile birlikte üretir.
 * UYAP çıktısı: "Toplam Alacak (Fiili Ödeme Tarihindeki Türk Lirası ...)"
 */
export function toplamAlacakEtiketi(serh: string): string {
    return serhEkle(TOPLAM_ALACAK_ETIKETI, serh);
}
